/**
 * Internal analytics client: batches and flushes events to our own backend.
 * Uses sendBeacon for reliability (fires even on page unload).
 */

import type {
  IngestAnalyticsEventsRequest,
  AnalyticsEventData,
  AnalyticsSessionContext,
} from '@tilly/shared';
import { getVisitorSessionIds, touchSession } from './visitorId';
import { isInternalAnalyticsBrowser, readOrganicAttribution } from './organicAttribution';

const BATCH_FLUSH_INTERVAL_MS = 5000; // 5 seconds
const MAX_EVENTS_PER_BATCH = 50;

type EventQueueEntry = AnalyticsEventData;

class InternalAnalyticsClient {
  private eventQueue: EventQueueEntry[] = [];
  private flushInterval: number | null = null;
  private sessionContext: AnalyticsSessionContext | null = null;
  private ingestUrl: string;
  private consentGranted = false;

  constructor(ingestUrl: string) {
    this.ingestUrl = ingestUrl;
    this.initSessionContext();
    this.startFlushTimer();
    this.setupPageVisibilityListener();
  }

  /**
   * Initialize the session context once per page load (capture UTMs, referrer, landing page).
   */
  private initSessionContext(): void {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const attribution = readOrganicAttribution();

    this.sessionContext = {
      referrer: document.referrer,
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      utm_term: params.get('utm_term') || undefined,
      utm_content: params.get('utm_content') || undefined,
      landing_page: url.pathname + url.search, // Capture the landing page path+search
      attribution_id: attribution?.id,
      attribution_tool_slug: attribution?.tool_slug,
      attribution_landing_page: attribution?.landing_page,
      attribution_captured_at: attribution?.captured_at,
      traffic_type: isInternalAnalyticsBrowser() ? 'internal' : 'external',
    };
  }

  /**
   * Set experiment_id and variant_id in the session context (called from ExperimentLandingPage).
   */
  public setExperimentContext(experimentId: string, variantId: string): void {
    if (this.sessionContext) {
      this.sessionContext.experiment_id = experimentId;
      this.sessionContext.variant_id = variantId;
    }
  }

  /**
   * Check and update consent status from localStorage (tilly_consent).
   */
  private updateConsentStatus(): void {
    try {
      const consent = localStorage.getItem('tilly_consent');
      this.consentGranted = consent === 'granted';
    } catch {
      this.consentGranted = false;
    }
  }

  /**
   * Enqueue a single event for batching.
   */
  public enqueueEvent(
    name: AnalyticsEventData['name'],
    params?: Record<string, unknown>,
    path?: string
  ): void {
    // Only track if consent is granted
    this.updateConsentStatus();
    if (!this.consentGranted) {
      return;
    }

    touchSession(); // Extend session idle timeout on any event
    const attribution = readOrganicAttribution();
    if (this.sessionContext && attribution) {
      this.sessionContext.attribution_id = attribution.id;
      this.sessionContext.attribution_tool_slug = attribution.tool_slug;
      this.sessionContext.attribution_landing_page = attribution.landing_page;
      this.sessionContext.attribution_captured_at = attribution.captured_at;
      this.sessionContext.traffic_type = isInternalAnalyticsBrowser() ? 'internal' : 'external';
    }

    const event: EventQueueEntry = {
      event_id: crypto.randomUUID(),
      name,
      path: path || window.location.pathname + window.location.search,
      params,
      timestamp: Date.now(),
    };

    this.eventQueue.push(event);

    // Flush immediately if we hit the batch limit
    if (this.eventQueue.length >= MAX_EVENTS_PER_BATCH) {
      this.flush();
    }
  }

  /**
   * Flush all queued events to the backend.
   */
  private flush(): void {
    if (this.eventQueue.length === 0 || !this.sessionContext) {
      return;
    }

    this.updateConsentStatus();
    if (!this.consentGranted) {
      this.eventQueue = [];
      return;
    }

    const { visitor_id, session_id, is_new_visitor } = getVisitorSessionIds();

    const payload: IngestAnalyticsEventsRequest = {
      visitor_id,
      session_id,
      is_new_visitor,
      session_context: this.sessionContext,
      events: this.eventQueue.splice(0, MAX_EVENTS_PER_BATCH), // Send up to MAX per request
    };

    try {
      // Use fetch with keepalive:true — same pattern as the rest of the app,
      // and keepalive lets the request outlive page navigation/unload events.
      fetch(this.ingestUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {
        // Silently fail; events are best-effort
      });
    } catch {
      // Silently fail
    }
  }

  /**
   * Set up periodic flushing.
   */
  private startFlushTimer(): void {
    this.flushInterval = window.setInterval(() => {
      this.flush();
    }, BATCH_FLUSH_INTERVAL_MS);
  }

  /**
   * Flush on page visibility change (when user switches tabs or closes page).
   */
  private setupPageVisibilityListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Page is being hidden; flush any pending events
        this.flush();
      }
    });

    // Also flush on beforeunload (backup measure)
    window.addEventListener('beforeunload', () => {
      this.flush();
    });
  }

  /**
   * Cleanup: stop the flush interval and flush any remaining events.
   */
  public destroy(): void {
    if (this.flushInterval !== null) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    this.flush();
  }
}

// Singleton instance
let client: InternalAnalyticsClient | null = null;

/**
 * Initialize the analytics client (should be called once per app load).
 */
export function initInternalAnalytics(ingestUrl: string): void {
  if (!client) {
    client = new InternalAnalyticsClient(ingestUrl);
  }
}

/**
 * Enqueue an event to be batched and flushed.
 */
export function trackInternalEvent(
  name: AnalyticsEventData['name'],
  params?: Record<string, unknown>,
  path?: string
): void {
  if (!client) {
    console.warn('Internal analytics client not initialized; call initInternalAnalytics first');
    return;
  }
  client.enqueueEvent(name, params, path);
}

/**
 * Set experiment context (called from ExperimentLandingPage).
 */
export function setInternalAnalyticsExperimentContext(
  experimentId: string,
  variantId: string
): void {
  if (!client) {
    return;
  }
  client.setExperimentContext(experimentId, variantId);
}

/**
 * Cleanup on unmount.
 */
export function destroyInternalAnalytics(): void {
  if (client) {
    client.destroy();
    client = null;
  }
}
